/**
 * Stat / table / pivot bodies for the add-tile dialog's live tile preview
 * (`AddTilePreview`). Pure rendering over the last Preview run's rows — the
 * same shaping utils + exported presentational pieces their tile views use
 * (SEVERITY_TEXT, CompareChip, StatSparkline, pivotTransform), so the preview
 * can't drift from the real tile.
 */
import { useMemo, type ReactNode } from 'react';
import type { QueryResultSet } from '@/types/queryExecution';
import { analyzeColumns } from '../../../charts/utils/columnTypeDetection';
import { computeStatCompare, resolveStatTileRefs, resolveStatValue } from '../../utils/statFormat';
import { thresholdEval } from '../../utils/thresholdEval';
import { pivotTransform, resolvePivotTileRefs } from '../../utils/pivotTransform';
import { CompareChip } from '../tiles/StatTileView';
import { buildSparklinePoints, SEVERITY_TEXT } from '../tiles/statTileData';
import { StatSparkline } from '../tiles/StatSparkline';
import { statCompareFromValues, statFormatFromValues, type AddTileFormValues } from './addTileFormModel';

export type PreviewBodyProps = {
  values: AddTileFormValues;
  resultSet: QueryResultSet;
};

export function PreviewHint({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center px-4 text-center text-xs text-muted-foreground">
      {children}
    </div>
  );
}

export function StatPreviewBody({ values, resultSet }: PreviewBodyProps) {
  const row = resultSet.rows[0];
  // Same resolution seam `StatTileView`/`alertValuePreview`/the HTML
  // snapshot use — the preview has no store-level columnInfos, so this run's
  // rows resolve a NAME-based (or whitespace-rekeyed) reference itself
  // (PR review, P2).
  const resolvedRefs = resolveStatTileRefs(
    {
      valueColumn: values.statValueColumn || undefined,
      compare: statCompareFromValues(values),
      sparkline:
        values.statSparkline && values.statSparkTime && values.statSparkValue
          ? { timeColumn: values.statSparkTime, valueColumn: values.statSparkValue }
          : undefined,
    },
    analyzeColumns(resultSet.columns, resultSet.rows),
  );
  const valueColumn = resolvedRefs.valueColumn;
  if (!valueColumn) return <PreviewHint>No numeric column to display.</PreviewHint>;
  const outcome = resolveStatValue(row, valueColumn, statFormatFromValues(values));
  if (!outcome.ok) {
    return (
      <PreviewHint>
        {outcome.error.kind === 'missing-column'
          ? `Column "${outcome.error.column}" was not found in the result`
          : `Column "${outcome.error.column}" is not numeric`}
      </PreviewHint>
    );
  }
  const severity = thresholdEval(
    outcome.raw,
    values.statThresholds.length ? values.statThresholds : undefined,
  );
  const compare = computeStatCompare(outcome.raw, resolvedRefs.compare, row);
  const sparkline = resolvedRefs.sparkline
    ? buildSparklinePoints(resultSet.rows, resolvedRefs.sparkline)
    : [];
  return (
    // Mirrors StatTileView's centered, container-scaled hero number verbatim
    // (same @container breakpoints, same truncate + title overflow guard) —
    // the preview's whole job is to show what the tile will look like, so any
    // divergence here is a lie. Only the alert badge is absent: no tile, no
    // rule yet.
    <div
      className="@container flex h-full flex-col items-center justify-center gap-1.5 px-4 py-2 text-center"
      data-testid="add-tile-stat-preview"
    >
      <div className="flex max-w-full items-end justify-center gap-2">
        <span
          className={`max-w-full truncate text-2xl leading-tight font-semibold tabular-nums @2xs:text-3xl @md:text-4xl @2xl:text-5xl ${SEVERITY_TEXT[severity]}`}
          data-severity={severity}
          title={outcome.text}
        >
          {outcome.text}
        </span>
        {sparkline.length >= 2 && <StatSparkline points={sparkline} severity={severity} />}
      </div>
      <CompareChip compare={compare} />
    </div>
  );
}

const TABLE_PREVIEW_ROWS = 8;

export function TablePreviewBody({ values, resultSet }: PreviewBodyProps) {
  const density = values.tableDensity === 'comfortable' ? 'py-1.5' : 'py-0.5';
  const cap =
    values.tableRowLimit > 0 ? Math.min(values.tableRowLimit, TABLE_PREVIEW_ROWS) : TABLE_PREVIEW_ROWS;
  const rows = resultSet.rows.slice(0, cap);
  return (
    <div className="h-full overflow-auto" data-testid="add-tile-table-preview">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-muted/60">
          <tr>
            {resultSet.columns.map((col) => (
              <th key={col.name} className={`px-2 text-left font-medium ${density}`}>
                {col.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-border/60">
              {resultSet.columns.map((col) => {
                const value = row[col.id ?? col.name];
                return (
                  <td key={col.name} className={`max-w-40 truncate px-2 ${density}`}>
                    {value === null || value === undefined ? (
                      <span className="italic text-muted-foreground">∅</span>
                    ) : (
                      String(value)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PIVOT_PREVIEW_COLUMNS = 5;
const PIVOT_PREVIEW_ROWS = 6;

export function PivotPreviewBody({ values, resultSet }: PreviewBodyProps) {
  const configured = Boolean(values.pivotRow && values.pivotColumn && values.pivotValue);
  const pivot = useMemo(() => {
    if (!configured || resultSet.rows.length === 0) return null;
    // Same resolution seam `PivotTileView`/the HTML snapshot use — the
    // preview has no store-level columnInfos, so this run's rows resolve a
    // NAME-based (or whitespace-rekeyed) reference itself (PR review, P2).
    const resolved = resolvePivotTileRefs(
      { rowColumn: values.pivotRow, columnColumn: values.pivotColumn, valueColumn: values.pivotValue },
      analyzeColumns(resultSet.columns, resultSet.rows),
    );
    return pivotTransform(resultSet.rows, { ...resolved, aggregation: values.pivotAgg });
  }, [configured, resultSet.rows, resultSet.columns, values.pivotRow, values.pivotColumn, values.pivotValue, values.pivotAgg]);
  if (!configured) return <PreviewHint>Pick row, column and value columns to pivot.</PreviewHint>;
  if (!pivot || pivot.rows.length === 0) {
    return <PreviewHint>No rows — the tile will show its empty state.</PreviewHint>;
  }
  const columnKeys = pivot.columnKeys.slice(0, PIVOT_PREVIEW_COLUMNS);
  const rows = pivot.rows.slice(0, PIVOT_PREVIEW_ROWS);
  const moreColumns = pivot.columnKeys.length - columnKeys.length;
  const moreRows = pivot.rows.length - rows.length;
  return (
    <div className="h-full overflow-auto" data-testid="add-tile-pivot-preview">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-muted/60">
          <tr>
            <th className="px-2 py-0.5 text-left font-medium">{values.pivotRow}</th>
            {columnKeys.map((col) => (
              <th key={col} className="px-2 py-0.5 text-right font-medium">
                {col}
              </th>
            ))}
            {moreColumns > 0 && (
              <th className="px-2 py-0.5 text-right font-medium text-muted-foreground">
                +{moreColumns} more
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.rowKey} className="border-t border-border/60">
              <th className="max-w-40 truncate px-2 py-0.5 text-left font-normal">{row.rowKey}</th>
              {columnKeys.map((col, i) => (
                <td key={col} className="px-2 py-0.5 text-right tabular-nums">
                  {row.cells[i] === null ? '∅' : row.cells[i]?.toLocaleString()}
                </td>
              ))}
              {moreColumns > 0 && <td />}
            </tr>
          ))}
        </tbody>
      </table>
      {moreRows > 0 && (
        <div className="px-2 py-1 text-[11px] text-muted-foreground">
          +{moreRows} more {moreRows === 1 ? 'row' : 'rows'} on the tile
        </div>
      )}
    </div>
  );
}
